'use client';

/**
 * AgentBuilderPage - Visual agent configuration and testing interface
 *
 * Provides a complete builder experience with:
 * - Tab-based configuration panel (Agent Config, MCP Servers, Sub Agents)
 * - Chat panel for testing the agent
 * - Save functionality to WFM
 */

import React, { useState, useEffect, useCallback, useMemo, lazy, Suspense } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Separator } from '@/components/ui/separator';
import { ThemeToggleSimple } from '@/components/ui/theme-toggle';
import { ThemeProvider } from '@/lib/theme-context';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  PlusIcon,
  XIcon,
  SaveIcon,
  PlayIcon,
  RefreshCwIcon,
  ServerIcon,
  BotIcon,
  SettingsIcon,
  Trash2Icon,
  ChevronDownIcon,
  ChevronUpIcon,
  WrenchIcon,
  MessageSquareIcon,
  SparklesIcon,
  AlertCircleIcon,
  CheckCircle2Icon,
  Loader2Icon,
  CodeIcon,
  CopyIcon,
  CheckIcon,
  Maximize2Icon,
  Minimize2Icon,
  FileEditIcon,
  UploadIcon,
  ArrowLeftIcon,
  Clock3Icon,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { setApiBaseUrl, setMcpBaseUrl } from '@/lib/apiClient';
import { initAuth, isAuthInitialized } from '@/lib/auth';
import { setIdentity, isIdentitySet } from '@/lib/config/services';


// Import agent builder types
import type {
  AgentConfig,
  AgentSDK,
  LLMProvider,
  ReasoningEffort,
  MCPServerBinding,
  SubAgentBinding,
  MCPServerListItem,
  MCPTool,
  WFMAgent,
  WFMWorkflow,
  BuilderTab,
  BuilderMode,
  BuilderView,
  SelectedToolConfig,
} from '@/types/agent-builder';
import {
  DEFAULT_AGENT_CONFIG,
  LLM_MODELS,
  SDK_OPTIONS,
  PROVIDER_OPTIONS,
  REASONING_OPTIONS,
} from '@/types/agent-builder';

// Import API clients
import { listMCPServers, listMCPTypes, getMCPServerDetails } from '@/lib/mcpGatewayClient';
import {
  listAgents,
  createFlowsAlphaAgent,
  wfmAgentToConfig,
  createDraft,
  updateDraft,
  loadDraft,
  listWorkflows,
  deleteDraft,
  workflowToConfig,
  listProjects,
  createDeployment,
  getDeployments,
  listLLMResources,
  type LLMResource,
  type DeploymentResponse,
} from '@/lib/wfmClient';

// =============================================================================
// TYPES
// =============================================================================

export interface AgentBuilderPageProps {
  /** Optional initial config (for editing existing agents) */
  initialConfig?: Partial<AgentConfig>;
  /** Initial agent to load (by ID) - opens in published mode */
  initialAgentId?: string;
  /** Initial workflow to load (by ID) - opens in draft mode */
  initialWorkflowId?: string;
  /** Project context */
  projectId?: string;
  /** User email (for WFM ownership) */
  userEmail?: string;
  /** Tenant ID */
  tenantId?: string;
  /** User ID */
  userId?: string;
  /** API base URL - required */
  apiBaseUrl: string;
  /** MCP base URL - required */
  mcpBaseUrl: string;
  /** External token provider */
  getToken?: () => Promise<string | null> | string | null;
  /** Callback when agent is published */
  onPublish?: (result: { workflow_id: string; agent_id: string; version: string; name: string }) => void;
  /** Callback when agent is saved (deprecated - use onPublish) */
  onSave?: (result: { workflow_id: string; agent_id: string; name: string }) => void;
  /** Callback when cancelled/exited */
  onCancel?: () => void;
  /** Callback when exiting to home */
  onExit?: () => void;
  /** Start directly in editor mode (skip home) */
  skipHome?: boolean;
  /** Custom class for container */
  className?: string;
}

// =============================================================================
// AGENT CONFIG FORM
// =============================================================================

interface AgentConfigFormProps {
  config: AgentConfig;
  onChange: (config: AgentConfig) => void;
  onExpandInstructions: () => void;
  availableLLMs: LLMResource[];
  isLLMsLoading: boolean;
}

// =============================================================================
// FULL SCREEN CONFIG FORM (for expanded view)
// =============================================================================

interface FullScreenConfigFormProps {
  config: AgentConfig;
  onChange: (config: AgentConfig) => void;
  availableLLMs: LLMResource[];
  isLLMsLoading: boolean;
}

function FullScreenConfigForm({ config, onChange, availableLLMs, isLLMsLoading }: FullScreenConfigFormProps) {
  const updateConfig = <K extends keyof AgentConfig>(key: K, value: AgentConfig[K]) => {
    onChange({ ...config, [key]: value });
  };

  const updateLLM = <K extends keyof AgentConfig['llm']>(key: K, value: AgentConfig['llm'][K]) => {
    onChange({
      ...config,
      llm: { ...config.llm, [key]: value },
    });
  };

  return (
    <div className="h-full flex">
      {/* Left Column - LLM Config */}
      <div className="w-1/3 p-6 space-y-8 bg-muted/20 overflow-auto">
        {/* LLM Configuration */}
        <div className="space-y-4">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            LLM Configuration
          </h3>
          <div>
            <Label htmlFor="model-full" className="text-sm font-medium">Select LLM Resource</Label>
            {isLLMsLoading ? (
              <div className="mt-2 h-10 flex items-center text-sm text-muted-foreground">
                <Loader2Icon className="h-4 w-4 mr-2 animate-spin" />
                Loading LLM resources...
              </div>
            ) : availableLLMs.length > 0 ? (
              <Select
                value={config.llm.res_id || config.llm.model}
                onValueChange={(value) => {
                  console.log('🔍 FullScreenConfigForm LLM selection - value:', value);
                  console.log('🔍 Available LLMs:', availableLLMs.map(l => ({ id: l.id, name: l.name })));
                  const llm = availableLLMs.find(l => l.id === value);
                  console.log('🔍 Found LLM:', llm);
                  if (llm) {
                    const newConfig = {
                      ...config,
                      llm: {
                        ...config.llm,
                        model: llm.name,
                        res_id: llm.id,
                        provider: (llm.provider?.toLowerCase().includes('anthropic') ? 'anthropic' : llm.provider?.toLowerCase().includes('openai') ? 'azure_openai' : config.llm.provider) as LLMProvider,
                      },
                    };
                    console.log('🔍 New config.llm:', newConfig.llm);
                    onChange(newConfig);
                  }
                }}
              >
                <SelectTrigger className="mt-2 h-10 bg-background">
                  <SelectValue placeholder="Choose an LLM resource..." />
                </SelectTrigger>
                <SelectContent>
                  {availableLLMs.map((llm) => (
                    <SelectItem key={llm.id} value={llm.id}>
                      {llm.display_name || llm.name} ({llm.provider})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <Select
                value={`${config.llm.provider}:${config.llm.model}`}
                onValueChange={(value) => {
                  const [provider, model] = value.split(':');
                  onChange({
                    ...config,
                    llm: { ...config.llm, provider: provider as LLMProvider, model },
                  });
                }}
              >
                <SelectTrigger className="mt-2 h-10 bg-background">
                  <SelectValue placeholder="Choose a model..." />
                </SelectTrigger>
                <SelectContent>
                  {PROVIDER_OPTIONS.map((provider) => (
                    <React.Fragment key={provider.value}>
                      {LLM_MODELS[provider.value as LLMProvider]?.map((model) => (
                        <SelectItem key={`${provider.value}:${model.value}`} value={`${provider.value}:${model.value}`}>
                          {provider.label} - {model.label}
                        </SelectItem>
                      ))}
                    </React.Fragment>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
          <div>
            <Label htmlFor="reasoning-full" className="text-sm font-medium">Reasoning Effort</Label>
            <Select
              value={config.llm.reasoning_effort || 'medium'}
              onValueChange={(value) => updateLLM('reasoning_effort', value as ReasoningEffort)}
            >
              <SelectTrigger className="mt-2 h-10 bg-background">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {REASONING_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground mt-2">
              Higher effort = more thorough but slower
            </p>
          </div>
        </div>
      </div>

      {/* Right Column - System Instructions (2/3 width) */}
      <div className="w-2/3 p-6 flex flex-col overflow-hidden">
        <div className="mb-4">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            System Instructions
          </h3>
          <p className="text-xs text-muted-foreground mt-1">
            Define how your agent should behave and respond to users
          </p>
        </div>
        <Textarea
          id="instructions-full"
          value={config.instructions}
          onChange={(e) => updateConfig('instructions', e.target.value)}
          placeholder="You are a helpful AI assistant. Be as detailed as possible when answering questions. Try to understand the intent behind each question and provide relevant additional information.

Use the tools available to you to answer the user's question. If you're unsure about something, ask for clarification.

Key behaviors:
- Be concise but thorough
- Cite sources when applicable
- Ask clarifying questions if needed
- Use available tools effectively"
          className="flex-1 font-mono text-sm bg-muted/30 resize-none rounded-lg"
        />
      </div>
    </div>
  );
}

// =============================================================================
// CONDENSED AGENT CONFIG FORM (for split view)
// =============================================================================

function AgentConfigForm({ config, onChange, onExpandInstructions, availableLLMs, isLLMsLoading }: AgentConfigFormProps) {
  const updateConfig = <K extends keyof AgentConfig>(key: K, value: AgentConfig[K]) => {
    onChange({ ...config, [key]: value });
  };

  const updateLLM = <K extends keyof AgentConfig['llm']>(key: K, value: AgentConfig['llm'][K]) => {
    onChange({
      ...config,
      llm: { ...config.llm, [key]: value },
    });
  };

  // Handle LLM resource selection
  return (
    <div className="p-5 space-y-6 bg-muted/30">
      {/* LLM Configuration */}
      <div className="space-y-3">
        <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
          LLM Configuration
        </h3>

        <div>
          <Label htmlFor="model" className="text-sm font-medium">Select LLM Resource</Label>
          {isLLMsLoading ? (
            <div className="mt-1.5 h-10 flex items-center text-sm text-muted-foreground">
              <Loader2Icon className="h-4 w-4 mr-2 animate-spin" />
              Loading...
            </div>
          ) : availableLLMs.length > 0 ? (
            <Select
              value={config.llm.res_id || config.llm.model}
              onValueChange={(value) => {
                console.log('🔍 AgentConfigForm LLM selection - value:', value);
                console.log('🔍 Available LLMs:', availableLLMs.map(l => ({ id: l.id, name: l.name })));
                const llm = availableLLMs.find(l => l.id === value);
                console.log('🔍 Found LLM:', llm);
                if (llm) {
                  const newConfig = {
                    ...config,
                    llm: {
                      ...config.llm,
                      model: llm.name,
                      res_id: llm.id,
                      provider: (llm.provider?.toLowerCase().includes('anthropic') ? 'anthropic' : llm.provider?.toLowerCase().includes('openai') ? 'azure_openai' : config.llm.provider) as LLMProvider,
                    },
                  };
                  console.log('🔍 New config.llm:', newConfig.llm);
                  onChange(newConfig);
                }
              }}
            >
              <SelectTrigger className="mt-1.5">
                <SelectValue placeholder="Choose an LLM resource..." />
              </SelectTrigger>
              <SelectContent>
                {availableLLMs.map((llm) => (
                  <SelectItem key={llm.id} value={llm.id}>
                    {llm.display_name || llm.name} ({llm.provider})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <Select
              value={`${config.llm.provider}:${config.llm.model}`}
              onValueChange={(value) => {
                const [provider, model] = value.split(':');
                onChange({
                  ...config,
                  llm: { ...config.llm, provider: provider as LLMProvider, model },
                });
              }}
            >
              <SelectTrigger className="mt-1.5">
                <SelectValue placeholder="Choose a model..." />
              </SelectTrigger>
              <SelectContent>
                {PROVIDER_OPTIONS.map((provider) => (
                  <React.Fragment key={provider.value}>
                    {LLM_MODELS[provider.value as LLMProvider]?.map((model) => (
                      <SelectItem key={`${provider.value}:${model.value}`} value={`${provider.value}:${model.value}`}>
                        {provider.label} - {model.label}
                      </SelectItem>
                    ))}
                  </React.Fragment>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        <div>
          <Label htmlFor="reasoning" className="text-sm font-medium">Reasoning Effort</Label>
          <Select
            value={config.llm.reasoning_effort || 'medium'}
            onValueChange={(value) => updateLLM('reasoning_effort', value as ReasoningEffort)}
          >
            <SelectTrigger className="mt-1.5">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {REASONING_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* System Instructions */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
            System Instructions
          </h3>
          <Button
            variant="ghost"
            size="sm"
            onClick={onExpandInstructions}
            className="h-6 px-2 text-xs"
          >
            <Maximize2Icon className="h-3 w-3 mr-1" />
            Expand
          </Button>
        </div>
        <div>
          <Textarea
            id="instructions"
            value={config.instructions}
            onChange={(e) => updateConfig('instructions', e.target.value)}
            placeholder="Be as detailed as possible when answering the question. Try to understand the intent of the question and try and provide any other information that might be helpful..."
            className="mt-1.5 font-mono text-sm bg-background min-h-[150px]"
          />
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// MCP SERVERS PANEL
// =============================================================================

interface MCPServersPanelProps {
  selectedServers: MCPServerBinding[];
  onAddServer: (server: MCPServerListItem, customHandle?: string) => void;
  onRemoveServer: (handle: string) => void;
  onUpdateServer: (handle: string, updates: Partial<MCPServerBinding>) => void;
  availableServers: MCPServerListItem[];
  mcpTypes: string[];
  toolsCache: Record<string, MCPTool[]>;
  isLoading: boolean;
  onRefresh: () => void;
  onLoadTools: (resourceId: string) => void;
  onSelectType: (type: string) => void;
  selectedMCPType: string | null;
  fullScreenMode?: boolean;
}

/**
 * Helper to generate tool name in agent.yaml format
 * Format: mcp__<handle>__<tool_name>
 */
function generateToolName(handle: string, toolName: string): string {
  // Handle is already sanitized when created
  return `mcp__${handle}__${toolName}`;
}

/**
 * Helper to check if a tool is selected
 */
function isToolSelected(server: MCPServerBinding, toolName: string): boolean {
  // If no selected_tools defined, all tools are selected by default
  if (!server.selected_tools) return true;
  return server.selected_tools.some(t => t.mcp_tool_name === toolName);
}

/**
 * Helper to get selected tool count
 */
function getSelectedToolCount(server: MCPServerBinding, totalTools: number): number {
  if (!server.selected_tools) return totalTools; // All selected by default
  return server.selected_tools.length;
}

function MCPServersPanel({
  selectedServers,
  onAddServer,
  onRemoveServer,
  onUpdateServer,
  availableServers,
  mcpTypes,
  toolsCache,
  isLoading,
  onRefresh,
  onLoadTools,
  onSelectType,
  selectedMCPType,
  fullScreenMode = false,
}: MCPServersPanelProps) {
  const [showAddModal, setShowAddModal] = useState(false);
  const [expandedServer, setExpandedServer] = useState<string | null>(null);
  const [editingHandle, setEditingHandle] = useState<string | null>(null);
  const [editHandleValue, setEditHandleValue] = useState('');
  // Track which step in the add modal: 'types' or 'instances'
  const [addModalStep, setAddModalStep] = useState<'types' | 'instances'>('types');

  // Handle tool selection toggle
  const handleToolToggle = (server: MCPServerBinding, tool: MCPTool, isChecked: boolean) => {
    const allTools = (server.res_id && toolsCache[server.res_id]) || [];

    let newSelectedTools: SelectedToolConfig[];

    if (!server.selected_tools) {
      // First time selecting/deselecting - initialize with all tools if checking, or all except this one
      if (isChecked) {
        // This shouldn't happen as all are selected by default
        newSelectedTools = allTools.map(t => ({
          name: generateToolName(server.handle, t.name),
          mcp_tool_name: t.name,
          description: t.description,
          async: false,
          timeout: 30,
        }));
      } else {
        // Deselecting one - create list with all except this one
        newSelectedTools = allTools
          .filter(t => t.name !== tool.name)
          .map(t => ({
            name: generateToolName(server.handle, t.name),
            mcp_tool_name: t.name,
            description: t.description,
            async: false,
            timeout: 30,
          }));
      }
    } else {
      if (isChecked) {
        // Add tool to selection
        newSelectedTools = [
          ...server.selected_tools,
          {
            name: generateToolName(server.handle, tool.name),
            mcp_tool_name: tool.name,
            description: tool.description,
            async: false,
            timeout: 30,
          },
        ];
      } else {
        // Remove tool from selection
        newSelectedTools = server.selected_tools.filter(t => t.mcp_tool_name !== tool.name);
      }
    }

    onUpdateServer(server.handle, { selected_tools: newSelectedTools });
  };

  // Handle select all / deselect all
  const handleSelectAll = (server: MCPServerBinding, selectAll: boolean) => {
    if (selectAll) {
      // Select all - set to undefined (means all selected)
      onUpdateServer(server.handle, { selected_tools: undefined });
    } else {
      // Deselect all - set empty array
      onUpdateServer(server.handle, { selected_tools: [] });
    }
  };

  // Handle editing
  const startEditingHandle = (server: MCPServerBinding) => {
    setEditingHandle(server.handle);
    setEditHandleValue(server.handle);
  };

  const saveHandle = (oldHandle: string) => {
    const newHandle = editHandleValue.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
    if (newHandle && newHandle !== oldHandle) {
      onUpdateServer(oldHandle, { handle: newHandle });
    }
    setEditingHandle(null);
    setEditHandleValue('');
  };

  return (
    <div className={cn("space-y-4", fullScreenMode ? "" : "p-4")}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ServerIcon className="h-4 w-4 text-muted-foreground" />
          <span className={cn("font-medium", fullScreenMode ? "text-base" : "text-sm")}>MCP Servers</span>
          <Badge variant="outline" className="text-xs">
            {selectedServers.length}
          </Badge>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={onRefresh}
            disabled={isLoading}
            className="h-8 w-8 p-0"
            title="Refresh servers"
          >
            <RefreshCwIcon className={cn("h-4 w-4", isLoading && "animate-spin")} />
          </Button>
          <Button
            variant={fullScreenMode ? "default" : "outline"}
            size="sm"
            onClick={() => setShowAddModal(true)}
            className={fullScreenMode ? "h-9" : "h-7 text-xs"}
          >
            <PlusIcon className="h-4 w-4 mr-1" />
            Add Server
          </Button>
        </div>
      </div>

      {/* Selected Servers List */}
      <div className={cn("space-y-2", fullScreenMode && "grid grid-cols-2 gap-4 space-y-0")}>
        {selectedServers.length === 0 ? (
          <div className={cn("text-center py-6 text-muted-foreground text-sm", fullScreenMode && "col-span-2 py-16")}>
            <ServerIcon className={cn("mx-auto mb-2 opacity-50", fullScreenMode ? "h-12 w-12 mb-4" : "h-8 w-8")} />
            <p className={fullScreenMode ? "text-base" : ""}>No MCP servers selected</p>
            <p className="text-xs mt-1">Click "Add Server" to give your agent tools</p>
          </div>
        ) : (
          selectedServers.map((server) => {
            // Use res_id for cache lookup since that's how tools are stored
            const tools = (server.res_id && toolsCache[server.res_id]) || [];
            const isExpanded = expandedServer === server.handle;
            const selectedCount = getSelectedToolCount(server, tools.length);
            const allSelected = !server.selected_tools || server.selected_tools.length === tools.length;
            const noneSelected = server.selected_tools && server.selected_tools.length === 0;
            const isEditingThisHandle = editingHandle === server.handle;

            return (
              <Card key={server.handle} className="p-3">
                <div className="flex items-center justify-between">
                  <div
                    className="flex items-center gap-2 cursor-pointer flex-1 min-w-0"
                    onClick={() => {
                      if (!isEditingThisHandle) {
                        setExpandedServer(isExpanded ? null : server.handle);
                        // Use res_id for cache lookup
                        if (!isExpanded && server.res_id && !toolsCache[server.res_id]) {
                          onLoadTools(server.res_id);
                        }
                      }
                    }}
                  >
                    <ChevronDownIcon className={cn("h-4 w-4 transition-transform flex-shrink-0", isExpanded && "rotate-180")} />
                    <div className="h-2 w-2 rounded-full bg-green-500 flex-shrink-0" />
                    <div className="flex flex-col min-w-0">
                      {isEditingThisHandle ? (
                        <input
                          type="text"
                          value={editHandleValue}
                          onChange={(e) => setEditHandleValue(e.target.value)}
                          onBlur={() => saveHandle(server.handle)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') saveHandle(server.handle);
                            if (e.key === 'Escape') {
                              setEditingHandle(null);
                              setEditHandleValue('');
                            }
                          }}
                          onClick={(e) => e.stopPropagation()}
                          className="text-sm font-medium font-mono bg-muted px-1 rounded border border-primary"
                          autoFocus
                        />
                      ) : (
                        <span
                          className="text-sm font-medium font-mono truncate cursor-text"
                          onClick={(e) => {
                            e.stopPropagation();
                            startEditingHandle(server);
                          }}
                          title="Click to edit handle"
                        >
                          {server.handle}
                        </span>
                      )}
                      <span className="text-[10px] text-muted-foreground truncate">
                        {server.serverName}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {tools.length > 0 && (
                      <Badge
                        variant={noneSelected ? "destructive" : "outline"}
                        className="text-[10px]"
                      >
                        {selectedCount}/{tools.length} tools
                      </Badge>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        onRemoveServer(server.handle);
                      }}
                      className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive"
                    >
                      <Trash2Icon className="h-3 w-3" />
                    </Button>
                  </div>
                </div>

                {/* Expanded Tools View with Checkboxes */}
                {isExpanded && (
                  <div className="mt-3 pt-3 border-t border-border">
                    {server.res_id && !toolsCache[server.res_id] ? (
                      <div className="text-center py-2 text-muted-foreground text-xs">
                        <Loader2Icon className="h-4 w-4 animate-spin mx-auto mb-1" />
                        Loading tools...
                      </div>
                    ) : tools.length === 0 ? (
                      <div className="text-center py-2 text-muted-foreground text-xs">
                        No tools available
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {/* Select All / Deselect All */}
                        <div className="flex items-center justify-between pb-2 border-b border-border">
                          <span className="text-xs text-muted-foreground">
                            {selectedCount} of {tools.length} tools selected
                          </span>
                          <div className="flex gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleSelectAll(server, true)}
                              disabled={allSelected}
                              className="h-6 text-[10px] px-2"
                            >
                              Select All
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleSelectAll(server, false)}
                              disabled={noneSelected}
                              className="h-6 text-[10px] px-2"
                            >
                              Deselect All
                            </Button>
                          </div>
                        </div>

                        {/* Tool List with Checkboxes */}
                        <div className="space-y-1 max-h-[300px] overflow-auto">
                          {tools.map((tool) => {
                            const isSelected = isToolSelected(server, tool.name);
                            return (
                              <div
                                key={tool.name}
                                className={cn(
                                  "flex items-start gap-2 p-2 rounded-md hover:bg-muted/50 transition-colors",
                                  !isSelected && "opacity-50"
                                )}
                              >
                                <Checkbox
                                  id={`${server.handle}-${tool.name}`}
                                  checked={isSelected}
                                  onCheckedChange={(checked) =>
                                    handleToolToggle(server, tool, checked === true)
                                  }
                                  className="mt-0.5"
                                />
                                <div className="flex-1 min-w-0">
                                  <label
                                    htmlFor={`${server.handle}-${tool.name}`}
                                    className="text-xs font-mono font-medium cursor-pointer flex items-center gap-1"
                                  >
                                    <WrenchIcon className="h-3 w-3 text-muted-foreground" />
                                    {tool.name}
                                  </label>
                                  {tool.description && (
                                    <p className="text-[10px] text-muted-foreground mt-0.5 line-clamp-2">
                                      {tool.description}
                                    </p>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </Card>
            );
          })
        )}
      </div>

      {/* Add Server Modal - Step 1: Types, Step 2: Instances */}
      <Dialog open={showAddModal} onOpenChange={(open) => {
        setShowAddModal(open);
        if (!open) {
          setAddModalStep('types');
          onSelectType('');
        }
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {addModalStep === 'types' ? 'Select Server Type' : 'Select MCP Server'}
            </DialogTitle>
            <DialogDescription>
              {addModalStep === 'types'
                ? 'Choose the type of MCP server to browse available instances.'
                : (
                  <span className="flex items-center gap-2">
                    <button
                      onClick={() => {
                        setAddModalStep('types');
                        onSelectType('');
                      }}
                      className="text-primary hover:underline"
                    >
                      &larr; Back to types
                    </button>
                    <span className="text-muted-foreground">|</span>
                    <Badge variant="outline" className="text-[10px]">{selectedMCPType}</Badge>
                  </span>
                )
              }
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[300px] overflow-auto space-y-2 py-4">
            {addModalStep === 'types' ? (
              /* Step 1: Show MCP types */
              mcpTypes.length === 0 ? (
                <div className="text-center py-4 text-muted-foreground text-sm">
                  {isLoading ? (
                    <span className="flex items-center justify-center gap-2">
                      <Loader2Icon className="h-4 w-4 animate-spin" />
                      Loading types...
                    </span>
                  ) : 'No server types available'}
                </div>
              ) : (
                mcpTypes.map((type) => (
                  <button
                    key={type}
                    onClick={() => {
                      onSelectType(type);
                      setAddModalStep('instances');
                    }}
                    className="w-full text-left p-3 rounded-lg border border-border hover:border-primary/30 hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <ServerIcon className="h-4 w-4 text-primary" />
                      <span className="font-medium text-sm">{type}</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {type === 'MCPServerStdio' ? 'Local stdio-based MCP servers' :
                       type === 'MCPServerStreamableHTTP' ? 'Remote HTTP-based MCP servers' :
                       `${type} MCP servers`}
                    </p>
                  </button>
                ))
              )
            ) : (
              /* Step 2: Show instances for selected type */
              isLoading ? (
                <div className="text-center py-4 text-muted-foreground text-sm">
                  <span className="flex items-center justify-center gap-2">
                    <Loader2Icon className="h-4 w-4 animate-spin" />
                    Loading servers...
                  </span>
                </div>
              ) : availableServers.length === 0 ? (
                <div className="text-center py-4 text-muted-foreground text-sm">
                  No servers found for type {selectedMCPType}
                </div>
              ) : (
                availableServers.map((server) => (
                  <button
                    key={server.id}
                    onClick={() => {
                      onAddServer(server);
                      setShowAddModal(false);
                      setAddModalStep('types');
                    }}
                    className="w-full text-left p-3 rounded-lg border border-border hover:border-primary/30 hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <div className="h-2 w-2 rounded-full bg-green-500" />
                      <span className="font-medium text-sm">{server.name}</span>
                    </div>
                    <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                      {server.type_template && (
                        <Badge variant="secondary" className="text-[10px]">
                          {server.type_template}
                        </Badge>
                      )}
                      <Badge variant="outline" className="text-[10px]">
                        {server.scope}
                      </Badge>
                    </div>
                    {server.description && (
                      <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                        {server.description}
                      </p>
                    )}
                  </button>
                ))
              )
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// =============================================================================
// FULL SCREEN MCP PANEL (1:3 split layout)
// =============================================================================

interface FullScreenMCPPanelProps {
  selectedServers: MCPServerBinding[];
  availableServers: MCPServerListItem[];
  mcpTypes: string[];
  toolsCache: Record<string, MCPTool[]>;
  isLoading: boolean;
  onAddServer: (server: MCPServerListItem, customHandle?: string) => void;
  onRemoveServer: (handle: string) => void;
  onUpdateServer: (handle: string, updates: Partial<MCPServerBinding>) => void;
  onRefresh: () => void;
  onLoadTools: (resourceId: string) => void;
  onSelectType: (type: string) => void;
  selectedMCPType: string | null;
}

function FullScreenMCPPanel({
  selectedServers,
  availableServers,
  mcpTypes,
  toolsCache,
  isLoading,
  onAddServer,
  onRemoveServer,
  onUpdateServer,
  onRefresh,
  onLoadTools,
  onSelectType,
  selectedMCPType,
}: FullScreenMCPPanelProps) {
  const [editingHandle, setEditingHandle] = useState<string | null>(null);
  const [editHandleValue, setEditHandleValue] = useState('');

  // Load tools when a server is added
  const handleAddServer = (server: MCPServerListItem) => {
    onAddServer(server);
    // Load tools for the newly added server
    if (server.id) {
      onLoadTools(server.id);
    }
  };

  // Handle editing
  const startEditingHandle = (server: MCPServerBinding) => {
    setEditingHandle(server.handle);
    setEditHandleValue(server.handle);
  };

  const saveHandle = (oldHandle: string) => {
    const newHandle = editHandleValue.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
    if (newHandle && newHandle !== oldHandle) {
      onUpdateServer(oldHandle, { handle: newHandle });
    }
    setEditingHandle(null);
    setEditHandleValue('');
  };

  // Handle tool selection toggle
  const handleToolToggle = (server: MCPServerBinding, tool: MCPTool, isChecked: boolean) => {
    const allTools = (server.res_id && toolsCache[server.res_id]) || [];

    let newSelectedTools: SelectedToolConfig[];

    if (!server.selected_tools) {
      // First time selecting/deselecting - initialize
      if (isChecked) {
        newSelectedTools = allTools.map(t => ({
          name: generateToolName(server.handle, t.name),
          mcp_tool_name: t.name,
          description: t.description,
          async: false,
          timeout: 30,
        }));
      } else {
        // Deselecting one - create list with all except this one
        newSelectedTools = allTools
          .filter(t => t.name !== tool.name)
          .map(t => ({
            name: generateToolName(server.handle, t.name),
            mcp_tool_name: t.name,
            description: t.description,
            async: false,
            timeout: 30,
          }));
      }
    } else {
      if (isChecked) {
        // Add tool to selection
        newSelectedTools = [
          ...server.selected_tools,
          {
            name: generateToolName(server.handle, tool.name),
            mcp_tool_name: tool.name,
            description: tool.description,
            async: false,
            timeout: 30,
          },
        ];
      } else {
        // Remove tool from selection
        newSelectedTools = server.selected_tools.filter(t => t.mcp_tool_name !== tool.name);
      }
    }

    onUpdateServer(server.handle, { selected_tools: newSelectedTools });
  };

  // Handle select all / deselect all
  const handleSelectAll = (server: MCPServerBinding, selectAll: boolean) => {
    if (selectAll) {
      onUpdateServer(server.handle, { selected_tools: undefined });
    } else {
      onUpdateServer(server.handle, { selected_tools: [] });
    }
  };

  return (
    <div className="h-full flex">
      {/* Left Panel - Types → Instances (1/4 width) */}
      <div className="w-1/4 p-4 bg-muted/20 overflow-auto border-r border-border">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            {selectedMCPType ? (
              <button
                onClick={() => onSelectType('')}
                className="text-primary hover:underline"
              >
                &larr; Types
              </button>
            ) : 'Server Types'}
          </h3>
          <Button
            variant="ghost"
            size="sm"
            onClick={onRefresh}
            disabled={isLoading}
            className="h-7 w-7 p-0"
          >
            <RefreshCwIcon className={cn("h-3.5 w-3.5", isLoading && "animate-spin")} />
          </Button>
        </div>

        {selectedMCPType && (
          <div className="mb-3">
            <Badge variant="outline" className="text-xs">
              {selectedMCPType}
            </Badge>
          </div>
        )}

        <div className="space-y-2">
          {!selectedMCPType ? (
            /* Show types */
            mcpTypes.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground text-xs">
                {isLoading ? 'Loading types...' : 'No types available'}
              </div>
            ) : (
              mcpTypes.map((type) => (
                <button
                  key={type}
                  onClick={() => onSelectType(type)}
                  className="w-full text-left p-3 rounded-lg bg-background hover:bg-primary/5 hover:border-primary/30 border border-transparent transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <ServerIcon className="h-4 w-4 text-primary shrink-0" />
                    <span className="text-sm font-medium truncate">{type}</span>
                  </div>
                  <p className="text-[10px] text-muted-foreground mt-1">
                    {type === 'MCPServerStdio' ? 'Local stdio-based servers' :
                     type === 'MCPServerStreamableHTTP' ? 'Remote HTTP servers' :
                     `${type} servers`}
                  </p>
                </button>
              ))
            )
          ) : (
            /* Show instances for selected type */
            isLoading ? (
              <div className="text-center py-8 text-muted-foreground text-xs">
                <Loader2Icon className="h-4 w-4 animate-spin mx-auto mb-2" />
                Loading servers...
              </div>
            ) : availableServers.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground text-xs">
                No servers for {selectedMCPType}
              </div>
            ) : (
              availableServers.map((server) => (
                <button
                  key={server.id}
                  onClick={() => handleAddServer(server)}
                  className="w-full text-left p-3 rounded-lg bg-background hover:bg-primary/5 hover:border-primary/30 border border-transparent transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <PlusIcon className="h-4 w-4 text-primary shrink-0" />
                    <span className="text-sm font-medium truncate">{server.name}</span>
                  </div>
                  <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                    {server.type_template && (
                      <Badge variant="outline" className="text-[10px]">
                        {server.type_template}
                      </Badge>
                    )}
                    <Badge variant="secondary" className="text-[10px]">
                      {server.scope}
                    </Badge>
                  </div>
                </button>
              ))
            )
          )}
        </div>
      </div>

      {/* Right Panel - Selected Servers & Tools (3/4 width) */}
      <div className="w-3/4 p-4 overflow-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Selected Servers & Tools
          </h3>
          <Badge variant="outline" className="text-xs">
            {selectedServers.length} server{selectedServers.length !== 1 ? 's' : ''}
          </Badge>
        </div>

        {selectedServers.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground">
            <ServerIcon className="h-12 w-12 mx-auto mb-4 opacity-30" />
            <p className="text-sm">No servers selected</p>
            <p className="text-xs mt-1">Click a server from the left panel to add it</p>
          </div>
        ) : (
          <div className="space-y-4">
            {selectedServers.map((server) => {
              const tools = (server.res_id && toolsCache[server.res_id]) || [];
              const isLoadingTools = server.res_id && !toolsCache[server.res_id];
              const selectedCount = getSelectedToolCount(server, tools.length);
              const allSelected = !server.selected_tools;
              const noneSelected = server.selected_tools && server.selected_tools.length === 0;
              const isEditingThisHandle = editingHandle === server.handle;

              return (
                <div key={server.handle} className="rounded-lg border border-border bg-card">
                  {/* Server Header */}
                  <div className="flex items-center justify-between p-3 border-b border-border">
                    <div className="flex items-center gap-2">
                      <div className="h-2 w-2 rounded-full bg-green-500" />
                      <div className="flex flex-col">
                        {isEditingThisHandle ? (
                          <input
                            type="text"
                            value={editHandleValue}
                            onChange={(e) => setEditHandleValue(e.target.value)}
                            onBlur={() => saveHandle(server.handle)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') saveHandle(server.handle);
                              if (e.key === 'Escape') {
                                setEditingHandle(null);
                                setEditHandleValue('');
                              }
                            }}
                            className="font-medium font-mono bg-muted px-1 rounded border border-primary"
                            autoFocus
                          />
                        ) : (
                          <span
                            className="font-medium font-mono cursor-text"
                            onClick={() => startEditingHandle(server)}
                            title="Click to edit handle"
                          >
                            {server.handle}
                          </span>
                        )}
                        <span className="text-[10px] text-muted-foreground">
                          {server.serverName}
                        </span>
                      </div>
                      <Badge variant="outline" className="text-[10px]">
                        {selectedCount}/{tools.length} tools
                      </Badge>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onRemoveServer(server.handle)}
                      className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
                    >
                      <XIcon className="h-4 w-4" />
                    </Button>
                  </div>

                  {/* Tools List */}
                  <div className="p-3">
                    {isLoadingTools ? (
                      <div className="flex items-center justify-center py-4 text-muted-foreground text-sm">
                        <Loader2Icon className="h-4 w-4 animate-spin mr-2" />
                        Loading tools...
                      </div>
                    ) : tools.length === 0 ? (
                      <div className="text-center py-4 text-muted-foreground text-sm">
                        No tools available
                      </div>
                    ) : (
                      <>
                        {/* Select All / Deselect All */}
                        <div className="flex items-center justify-between mb-3 pb-2 border-b border-border">
                          <span className="text-xs text-muted-foreground">
                            {selectedCount} of {tools.length} tools selected
                          </span>
                          <div className="flex gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleSelectAll(server, true)}
                              disabled={allSelected}
                              className="h-6 text-xs px-2"
                            >
                              Select All
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleSelectAll(server, false)}
                              disabled={noneSelected}
                              className="h-6 text-xs px-2"
                            >
                              Deselect All
                            </Button>
                          </div>
                        </div>

                        {/* Tools Grid */}
                        <div className="grid grid-cols-2 gap-2 max-h-[200px] overflow-auto">
                          {tools.map((tool) => {
                            const isSelected = isToolSelected(server, tool.name);
                            return (
                              <label
                                key={tool.name}
                                className={cn(
                                  "flex items-start gap-2 p-2 rounded-md cursor-pointer transition-colors",
                                  isSelected ? "bg-primary/5" : "bg-muted/30 opacity-60",
                                  "hover:bg-primary/10"
                                )}
                              >
                                <Checkbox
                                  checked={isSelected}
                                  onCheckedChange={(checked) =>
                                    handleToolToggle(server, tool, checked === true)
                                  }
                                  className="mt-0.5"
                                />
                                <div className="flex-1 min-w-0">
                                  <div className="text-xs font-medium truncate">{tool.name}</div>
                                  {tool.description && (
                                    <p className="text-[10px] text-muted-foreground mt-0.5 line-clamp-1">
                                      {tool.description}
                                    </p>
                                  )}
                                </div>
                              </label>
                            );
                          })}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// FULL SCREEN SUB AGENTS PANEL (1:3 split layout)
// =============================================================================

interface FullScreenSubAgentsPanelProps {
  selectedAgents: SubAgentBinding[];
  availableAgents: WFMAgent[];
  isLoading: boolean;
  onAddAgent: (agent: WFMAgent, deploymentId?: string) => void;
  onRemoveAgent: (agentId: string) => void;
  onUpdateAgent: (agentId: string, updates: Partial<SubAgentBinding>) => void;
  onRefresh: () => void;
}

function FullScreenSubAgentsPanel({
  selectedAgents,
  availableAgents,
  isLoading,
  onAddAgent,
  onRemoveAgent,
  onUpdateAgent,
  onRefresh,
}: FullScreenSubAgentsPanelProps) {
  // Track which agent we're picking deployments for
  const [pickingDeploymentsFor, setPickingDeploymentsFor] = useState<WFMAgent | null>(null);
  const [agentDeployments, setAgentDeployments] = useState<DeploymentResponse[]>([]);
  const [isDeploymentsLoading, setIsDeploymentsLoading] = useState(false);

  // Filter out already selected agents
  const unselectedAgents = availableAgents.filter(
    (a) => !selectedAgents.find((sel) => sel.agent_id === a.agent_id)
  );

  // When user clicks an agent, load its deployments
  const handleAgentClick = async (agent: WFMAgent) => {
    setPickingDeploymentsFor(agent);
    setIsDeploymentsLoading(true);
    try {
      const deployments = await getDeployments(agent.agent_id);
      setAgentDeployments(deployments);
    } catch (error) {
      console.error('Failed to load deployments for agent:', error);
      setAgentDeployments([]);
    } finally {
      setIsDeploymentsLoading(false);
    }
  };

  return (
    <div className="h-full flex">
      {/* Left Panel - Available Agents (1/4 width) */}
      <div className="w-1/4 p-4 bg-muted/20 overflow-auto border-r border-border">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            {pickingDeploymentsFor ? (
              <button
                onClick={() => {
                  setPickingDeploymentsFor(null);
                  setAgentDeployments([]);
                }}
                className="text-primary hover:underline"
              >
                &larr; Agents
              </button>
            ) : 'Available Agents'}
          </h3>
          <Button
            variant="ghost"
            size="sm"
            onClick={onRefresh}
            disabled={isLoading}
            className="h-7 w-7 p-0"
          >
            <RefreshCwIcon className={cn("h-3.5 w-3.5", isLoading && "animate-spin")} />
          </Button>
        </div>

        <div className="space-y-2">
          {pickingDeploymentsFor ? (
            /* Deployment picker for selected agent */
            <>
              <div className="p-2 rounded-lg bg-primary/5 border border-primary/20 mb-3">
                <div className="flex items-center gap-2">
                  <BotIcon className="h-4 w-4 text-primary" />
                  <span className="text-sm font-medium">{pickingDeploymentsFor.name}</span>
                </div>
                <p className="text-[10px] text-muted-foreground mt-1">Select a deployment</p>
              </div>
              {isDeploymentsLoading ? (
                <div className="text-center py-4 text-muted-foreground text-xs">
                  <Loader2Icon className="h-4 w-4 animate-spin mx-auto mb-2" />
                  Loading deployments...
                </div>
              ) : agentDeployments.length === 0 ? (
                <div className="text-center py-4 text-xs">
                  <p className="text-muted-foreground">No deployments found</p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-2 text-xs"
                    onClick={() => {
                      onAddAgent(pickingDeploymentsFor);
                      setPickingDeploymentsFor(null);
                      setAgentDeployments([]);
                    }}
                  >
                    Add without deployment
                  </Button>
                </div>
              ) : (
                agentDeployments.map((dep) => (
                  <button
                    key={dep.deployment_id}
                    onClick={() => {
                      onAddAgent(pickingDeploymentsFor, dep.deployment_id);
                      setPickingDeploymentsFor(null);
                      setAgentDeployments([]);
                    }}
                    className="w-full text-left p-3 rounded-lg bg-background hover:bg-primary/5 hover:border-primary/30 border border-transparent transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <PlusIcon className="h-4 w-4 text-primary shrink-0" />
                      <span className="text-sm font-medium truncate">{dep.name}</span>
                    </div>
                    <p className="text-[10px] text-muted-foreground mt-1 font-mono truncate">
                      {dep.deployment_id}
                    </p>
                  </button>
                ))
              )}
            </>
          ) : (
            /* Agent list */
            unselectedAgents.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground text-xs">
                {availableAgents.length === 0 ? 'Loading agents...' : 'All agents added'}
              </div>
            ) : (
              unselectedAgents.map((agent) => (
                <button
                  key={agent.agent_id}
                  onClick={() => handleAgentClick(agent)}
                  className="w-full text-left p-3 rounded-lg bg-background hover:bg-primary/5 hover:border-primary/30 border border-transparent transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <PlusIcon className="h-4 w-4 text-primary shrink-0" />
                    <span className="text-sm font-medium truncate">{agent.name}</span>
                  </div>
                  {agent.version && (
                    <Badge variant="secondary" className="text-[10px] mt-1.5">
                      v{agent.version}
                    </Badge>
                  )}
                </button>
              ))
            )
          )}
        </div>
      </div>

      {/* Right Panel - Selected Agents (3/4 width) */}
      <div className="w-3/4 p-4 overflow-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Selected Sub Agents
          </h3>
          <Badge variant="outline" className="text-xs">
            {selectedAgents.length} agent{selectedAgents.length !== 1 ? 's' : ''}
          </Badge>
        </div>

        {selectedAgents.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground">
            <BotIcon className="h-12 w-12 mx-auto mb-4 opacity-30" />
            <p className="text-sm">No sub agents selected</p>
            <p className="text-xs mt-1">Click an agent from the left panel to add it</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-4">
            {selectedAgents.map((agent) => (
              <div key={agent.agent_id} className="rounded-lg border border-border bg-card p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <BotIcon className="h-5 w-5 text-primary" />
                    <span className="font-medium">{agent.name}</span>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onRemoveAgent(agent.agent_id)}
                    className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
                  >
                    <XIcon className="h-4 w-4" />
                  </Button>
                </div>
                {agent.description && (
                  <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
                    {agent.description}
                  </p>
                )}
                <div className="mt-2">
                  {agent.deployment_id ? (
                    <Badge variant="outline" className="text-[10px]">
                      Deployment: {agent.deployment_id.slice(0, 8)}...
                    </Badge>
                  ) : (
                    <Badge variant="destructive" className="text-[10px]">
                      No deployment
                    </Badge>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// SUB AGENTS PANEL (condensed view)
// =============================================================================

interface SubAgentsPanelProps {
  selectedAgents: SubAgentBinding[];
  onAddAgent: (agent: WFMAgent, deploymentId?: string) => void;
  onRemoveAgent: (agentId: string) => void;
  availableAgents: WFMAgent[];
  isLoading: boolean;
  onRefresh: () => void;
  fullScreenMode?: boolean;
}

function SubAgentsPanel({
  selectedAgents,
  onAddAgent,
  onRemoveAgent,
  availableAgents,
  isLoading,
  onRefresh,
  fullScreenMode = false,
}: SubAgentsPanelProps) {
  const [showAddModal, setShowAddModal] = useState(false);
  // Two-step modal: 'agents' then 'deployments'
  const [addModalStep, setAddModalStep] = useState<'agents' | 'deployments'>('agents');
  const [pickingAgent, setPickingAgent] = useState<WFMAgent | null>(null);
  const [agentDeployments, setAgentDeployments] = useState<DeploymentResponse[]>([]);
  const [isDeploymentsLoading, setIsDeploymentsLoading] = useState(false);

  // Filter out already selected agents
  const unselectedAgents = availableAgents.filter(
    (a) => !selectedAgents.find((sel) => sel.agent_id === a.agent_id)
  );

  // Handle agent click → load deployments
  const handleAgentClick = async (agent: WFMAgent) => {
    setPickingAgent(agent);
    setAddModalStep('deployments');
    setIsDeploymentsLoading(true);
    try {
      const deployments = await getDeployments(agent.agent_id);
      setAgentDeployments(deployments);
    } catch (error) {
      console.error('Failed to load deployments:', error);
      setAgentDeployments([]);
    } finally {
      setIsDeploymentsLoading(false);
    }
  };

  return (
    <div className={cn("space-y-4", fullScreenMode ? "" : "p-4")}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <BotIcon className="h-4 w-4 text-muted-foreground" />
          <span className={cn("font-medium", fullScreenMode ? "text-base" : "text-sm")}>Sub Agents</span>
          <Badge variant="outline" className="text-xs">
            {selectedAgents.length}
          </Badge>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={onRefresh}
            disabled={isLoading}
            className="h-8 w-8 p-0"
            title="Refresh agents"
          >
            <RefreshCwIcon className={cn("h-4 w-4", isLoading && "animate-spin")} />
          </Button>
          <Button
            variant={fullScreenMode ? "default" : "outline"}
            size="sm"
            onClick={() => setShowAddModal(true)}
            className={fullScreenMode ? "h-9" : "h-7 text-xs"}
          >
            <PlusIcon className="h-4 w-4 mr-1" />
            Add Agent
          </Button>
        </div>
      </div>

      {/* Selected Agents List */}
      <div className={cn("space-y-2", fullScreenMode && "grid grid-cols-2 gap-4 space-y-0")}>
        {selectedAgents.length === 0 ? (
          <div className={cn("text-center py-6 text-muted-foreground text-sm", fullScreenMode && "col-span-2 py-16")}>
            <BotIcon className={cn("mx-auto mb-2 opacity-50", fullScreenMode ? "h-12 w-12 mb-4" : "h-8 w-8")} />
            <p className={fullScreenMode ? "text-base" : ""}>No sub-agents selected</p>
            <p className="text-xs mt-1">Click "Add Agent" to enable delegation</p>
          </div>
        ) : (
          selectedAgents.map((agent) => (
            <Card key={agent.agent_id} className="p-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <BotIcon className="h-4 w-4 text-primary" />
                  <span className="text-sm font-medium">{agent.name}</span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onRemoveAgent(agent.agent_id)}
                  className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive"
                >
                  <Trash2Icon className="h-3 w-3" />
                </Button>
              </div>
              {agent.description && (
                <p className="text-xs text-muted-foreground mt-2 line-clamp-2">
                  {agent.description}
                </p>
              )}
              <div className="mt-2">
                {agent.deployment_id ? (
                  <Badge variant="outline" className="text-[10px]">
                    Deployment: {agent.deployment_id.slice(0, 8)}...
                  </Badge>
                ) : (
                  <Badge variant="destructive" className="text-[10px]">
                    No deployment
                  </Badge>
                )}
              </div>
            </Card>
          ))
        )}
      </div>

      {/* Add Agent Modal - Step 1: Agents, Step 2: Deployments */}
      <Dialog open={showAddModal} onOpenChange={(open) => {
        setShowAddModal(open);
        if (!open) {
          setAddModalStep('agents');
          setPickingAgent(null);
          setAgentDeployments([]);
        }
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {addModalStep === 'agents' ? 'Select Sub Agent' : 'Select Deployment'}
            </DialogTitle>
            <DialogDescription>
              {addModalStep === 'agents'
                ? 'Choose an agent to add as a sub-agent'
                : (
                  <span className="flex items-center gap-2">
                    <button
                      onClick={() => {
                        setAddModalStep('agents');
                        setPickingAgent(null);
                        setAgentDeployments([]);
                      }}
                      className="text-primary hover:underline"
                    >
                      &larr; Back to agents
                    </button>
                    <span className="text-muted-foreground">|</span>
                    <Badge variant="outline" className="text-[10px]">{pickingAgent?.name}</Badge>
                  </span>
                )
              }
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[300px] overflow-auto space-y-2 py-4">
            {addModalStep === 'agents' ? (
              /* Step 1: Agent list */
              unselectedAgents.length === 0 ? (
                <div className="text-center py-4 text-muted-foreground text-sm">
                  No more agents available
                </div>
              ) : (
                unselectedAgents.map((agent) => (
                  <button
                    key={agent.agent_id}
                    onClick={() => handleAgentClick(agent)}
                    className="w-full text-left p-3 rounded-lg border border-border hover:border-primary/30 hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <BotIcon className="h-4 w-4 text-primary" />
                      <span className="font-medium text-sm">{agent.name}</span>
                      {agent.version && (
                        <Badge variant="secondary" className="text-[10px]">
                          v{agent.version}
                        </Badge>
                      )}
                    </div>
                    {agent.description && (
                      <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                        {agent.description}
                      </p>
                    )}
                  </button>
                ))
              )
            ) : (
              /* Step 2: Deployment list for selected agent */
              isDeploymentsLoading ? (
                <div className="text-center py-4 text-muted-foreground text-sm">
                  <span className="flex items-center justify-center gap-2">
                    <Loader2Icon className="h-4 w-4 animate-spin" />
                    Loading deployments...
                  </span>
                </div>
              ) : agentDeployments.length === 0 ? (
                <div className="text-center py-4 text-sm">
                  <p className="text-muted-foreground">No deployments found for {pickingAgent?.name}</p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-3 text-xs"
                    onClick={() => {
                      if (pickingAgent) {
                        onAddAgent(pickingAgent);
                        setShowAddModal(false);
                        setAddModalStep('agents');
                        setPickingAgent(null);
                        setAgentDeployments([]);
                      }
                    }}
                  >
                    Add without deployment
                  </Button>
                </div>
              ) : (
                agentDeployments.map((dep) => (
                  <button
                    key={dep.deployment_id}
                    onClick={() => {
                      if (pickingAgent) {
                        onAddAgent(pickingAgent, dep.deployment_id);
                      }
                      setShowAddModal(false);
                      setAddModalStep('agents');
                      setPickingAgent(null);
                      setAgentDeployments([]);
                    }}
                    className="w-full text-left p-3 rounded-lg border border-border hover:border-primary/30 hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <CheckCircle2Icon className="h-4 w-4 text-green-500" />
                      <span className="font-medium text-sm">{dep.name}</span>
                    </div>
                    <p className="text-[10px] text-muted-foreground mt-1 font-mono">
                      {dep.deployment_id}
                    </p>
                  </button>
                ))
              )
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// =============================================================================
// CHAT TEST PANEL (federated from agent-chat-web)
// =============================================================================

// Lazy-load the federated ChatArea from agent-chat-web
const FederatedChatArea = lazy(() => import('agentChatWeb/FederatedChatArea'));

interface ChatTestPanelProps {
  config: AgentConfig;
  enabled: boolean;
  onEnable: () => void;
  tenantId: string;
  projectId: string;
  userId: string;
  apiBaseUrl: string;
  /** Saved agent ID from WFM - use this instead of inline config when available */
  savedAgentId?: string;
  /** Available LLM resources for looking up resource ID */
  availableLLMs: LLMResource[];
  /** Token provider for auth */
  getToken?: () => Promise<string | null> | string | null;
}

function ChatTestPanel({
  config,
  enabled,
  onEnable,
  tenantId,
  projectId,
  userId,
  apiBaseUrl,
  savedAgentId,
  availableLLMs,
  getToken: getTokenProp,
}: ChatTestPanelProps) {
  const [chatKey, setChatKey] = useState(0);
  const [authToken, setAuthToken] = useState<string | undefined>();

  // Resolve token from provider
  useEffect(() => {
    if (!getTokenProp) return;
    const resolve = async () => {
      try {
        const token = await getTokenProp();
        if (token) setAuthToken(token);
      } catch (e) {
        console.warn('[ChatTestPanel] Failed to get auth token:', e);
      }
    };
    resolve();
    // Refresh token periodically (every 30s)
    const interval = setInterval(resolve, 30000);
    return () => clearInterval(interval);
  }, [getTokenProp]);

  const handleNewChat = () => {
    setChatKey((k) => k + 1);
  };

  // Derive agent ID and WS base URL
  const agentId = savedAgentId || `inline/${config.name || 'test-agent'}`;
  const wsBaseUrl = apiBaseUrl.replace(/^http/, 'ws');

  // Build inline agent YAML from config (only when no saved agent)
  const agentYaml = useMemo(() => {
    if (savedAgentId) return undefined;
    const tools: Array<string | { name: string; agent?: string; description?: string }> = ['ask_user'];
    config.sub_agents?.forEach(subagent => {
      const toolName = subagent.name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
      tools.push({
        name: toolName,
        agent: subagent.agent_id,
        description: subagent.description || '',
      });
    });
    return {
      name: config.name || 'Test Agent',
      description: config.description,
      sdk: 'eureka_agent' as const,
      llm: { required: true },
      tools,
      mcp_servers: config.mcp_servers?.map(server => ({
        name: server.handle,
        type_template: server.type_template,
        required: true,
      })),
    };
  }, [savedAgentId, config]);

  // Build resource bindings from config (LLM + MCP servers + sub-agents)
  const agentConfiguration = useMemo(() => {
    if (savedAgentId) return undefined;
    const bindings: Record<string, string> = {};
    if (config.llm?.res_id) {
      bindings['llm'] = config.llm.res_id;
    }
    config.mcp_servers?.forEach(server => {
      if (server.res_id) bindings[server.handle] = server.res_id;
    });
    config.sub_agents?.forEach(subagent => {
      if (subagent.deployment_id) {
        const toolName = subagent.name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
        bindings[toolName] = subagent.deployment_id;
      }
    });
    return Object.keys(bindings).length > 0 ? { bindings: { resource_bindings: bindings } } : undefined;
  }, [savedAgentId, config]);

  const promptMd = savedAgentId ? undefined : config.instructions;

  if (!enabled) {
    return (
      <div className="flex flex-col h-full bg-blue-50/50 dark:bg-muted/20">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 bg-background border-b">
          <h2 className="text-base font-medium">Try your Agent</h2>
          <Button variant="ghost" size="sm" className="text-xs" disabled>
            <RefreshCwIcon className="h-3 w-3 mr-1" />
            New Chat
          </Button>
        </div>
        {/* Empty State */}
        <div className="flex-1 flex flex-col items-center justify-center p-6">
          <Button
            onClick={onEnable}
            size="lg"
            className="px-8"
          >
            Start chatting
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-blue-50/50 dark:bg-muted/20">
      {/* Chat Header */}
      <div className="flex items-center justify-between px-6 py-4 bg-background border-b">
        <h2 className="text-base font-medium">Try your Agent</h2>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleNewChat}
          className="text-xs"
        >
          <RefreshCwIcon className="h-3 w-3 mr-1" />
          New Chat
        </Button>
      </div>

      {/* Federated Chat Interface */}
      <div className="flex-1 overflow-hidden">
        <Suspense fallback={<div className="flex items-center justify-center h-full text-muted-foreground">Loading chat...</div>}>
          <FederatedChatArea
            key={chatKey}
            tenantId={tenantId}
            projectId={projectId}
            userId={userId}
            agentId={agentId}
            wsBaseUrl={wsBaseUrl}
            apiBaseUrl={apiBaseUrl}
            authToken={authToken}
            developerMode={false}
            agentYaml={agentYaml}
            promptMd={promptMd}
            agentConfiguration={agentConfiguration}
          />
        </Suspense>
      </div>
    </div>
  );
}

// =============================================================================
// YAML PREVIEW MODAL
// =============================================================================

/**
 * Generate tool name in mcp__handle__tool format
 */
function generateMCPToolName(handle: string, toolName: string): string {
  // Handle is already sanitized when created
  return `mcp__${handle}__${toolName}`;
}

/**
 * Generate YAML representation of agent config
 */
function generateAgentYaml(config: AgentConfig, toolsCache?: Record<string, MCPTool[]>): string {
  const lines: string[] = [];

  lines.push(`name: ${config.name || 'unnamed'}`);
  lines.push(`description: ${config.description || ''}`);
  lines.push(`version: ${config.version || '1.0.0'}`);
  lines.push(`sdk: eureka_agent`); // Always eureka_agent for WFM-loaded agents
  lines.push('');

  // LLM config
  lines.push('llm:');
  lines.push(`  model: ${config.llm.model}`);
  lines.push(`  provider: ${config.llm.provider}`);
  if (config.llm.temperature !== undefined) {
    lines.push(`  temperature: ${config.llm.temperature}`);
  }
  if (config.llm.reasoning_effort) {
    lines.push(`  reasoning_effort: ${config.llm.reasoning_effort}`);
  }
  lines.push('');

  // prompt_md is what the executor actually reads for system prompt
  lines.push('prompt_md: |');
  const instructionLines = (config.instructions || '').split('\n');
  instructionLines.forEach(line => {
    lines.push(`  ${line}`);
  });
  lines.push('');

  // MCP Servers - declare what agent NEEDS (no actual res_ids)
  // agent.yaml format:
  //   mcp_servers:
  //     - name: <handle>               # user-defined unique name (lookup key in bindings)
  //       type_template: <template>    # functional template name (what kind of MCP server)
  //       required: true
  if (config.mcp_servers && config.mcp_servers.length > 0) {
    lines.push('mcp_servers:');
    config.mcp_servers.forEach(server => {
      lines.push(`  - name: ${server.handle}`);  // User-defined handle
      if (server.type_template) {
        lines.push(`    type_template: ${server.type_template}`);
      }
      lines.push(`    required: true`);

      // Add selected_tools with raw MCP tool names (not prefixed)
      if (server.selected_tools && server.selected_tools.length > 0) {
        lines.push('    selected_tools:');
        server.selected_tools.forEach(tool => {
          // Use mcp_tool_name (raw name) not the prefixed name
          lines.push(`      - ${tool.mcp_tool_name}`);
        });
      }
    });
    lines.push('');
  }

  // Runtime bindings section - always show if we have any bindings
  // Maps handle/tool_name -> instance UUID (resource_id or deployment_id)
  const hasLLMBinding = !!config.llm?.res_id;
  const hasMCPBindings = config.mcp_servers?.some(s => s.res_id);
  const hasSubAgentBindings = config.sub_agents?.some(s => s.deployment_id);
  if (hasLLMBinding || hasMCPBindings || hasSubAgentBindings) {
    lines.push('# Runtime bindings (handle -> resource_id/deployment_id)');
    lines.push('bindings:');
    lines.push('  resource_bindings:');
    if (config.llm?.res_id) {
      lines.push(`    llm: ${config.llm.res_id}`);
    }
    config.mcp_servers?.forEach(server => {
      if (server.res_id) {
        lines.push(`    ${server.handle}: ${server.res_id}`);
      }
    });
    config.sub_agents?.forEach(subagent => {
      if (subagent.deployment_id) {
        const toolName = subagent.name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
        lines.push(`    ${toolName}: ${subagent.deployment_id}`);
      }
    });
    lines.push('');
  }

  // Sub agents are declared as tools with "agent:" field
  // The executor processes tools with agent: field as agent-tools (subagent invocations)
  // async/timeout are implicit - agent-tools route through run_agent internally
  // description is optional - falls back to target agent's description
  if (config.sub_agents && config.sub_agents.length > 0) {
    lines.push('tools:');
    config.sub_agents.forEach(subagent => {
      // Tool name is derived from the subagent name (snake_case)
      const toolName = subagent.name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
      lines.push(`  - name: ${toolName}`);
      lines.push(`    agent: ${subagent.agent_id}`);
    });
  }

  return lines.join('\n');
}

interface YamlPreviewModalProps {
  open: boolean;
  onClose: () => void;
  config: AgentConfig;
  toolsCache?: Record<string, MCPTool[]>;
}

function YamlPreviewModal({ open, onClose, config, toolsCache }: YamlPreviewModalProps) {
  const [copied, setCopied] = useState(false);
  const yaml = useMemo(() => generateAgentYaml(config, toolsCache), [config, toolsCache]);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(yaml);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CodeIcon className="h-5 w-5" />
            Agent YAML Preview
          </DialogTitle>
          <DialogDescription>
            This is the generated agent.yaml configuration
          </DialogDescription>
        </DialogHeader>

        <div className="relative">
          <Button
            variant="outline"
            size="sm"
            className="absolute top-2 right-2 z-10"
            onClick={handleCopy}
          >
            {copied ? (
              <>
                <CheckIcon className="h-4 w-4 mr-1" />
                Copied
              </>
            ) : (
              <>
                <CopyIcon className="h-4 w-4 mr-1" />
                Copy
              </>
            )}
          </Button>
          <pre className="bg-muted rounded-lg p-4 overflow-auto max-h-[50vh] text-xs font-mono whitespace-pre-wrap">
            {yaml}
          </pre>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// =============================================================================
// INSTRUCTIONS MODAL
// =============================================================================

interface InstructionsModalProps {
  open: boolean;
  onClose: () => void;
  instructions: string;
  onChange: (instructions: string) => void;
}

function InstructionsModal({ open, onClose, instructions, onChange }: InstructionsModalProps) {
  const [value, setValue] = useState(instructions);

  // Sync with external value when modal opens
  useEffect(() => {
    if (open) {
      setValue(instructions);
    }
  }, [open, instructions]);

  const handleSave = () => {
    onChange(value);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-4xl h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>System Instructions</DialogTitle>
          <DialogDescription>
            Define how your agent should behave and respond to users. These instructions are sent to the LLM as the system prompt.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-hidden py-4">
          <Textarea
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="You are a helpful AI assistant. Be as detailed as possible when answering questions. Try to understand the intent behind each question and provide relevant additional information.

Use the tools available to you to answer the user's question. If you're unsure about something, ask for clarification.

Key behaviors:
- Be concise but thorough
- Cite sources when applicable
- Ask clarifying questions if needed
- Use available tools effectively"
            className="h-full font-mono text-sm bg-muted/30 resize-none rounded-lg"
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave}>
            Save Instructions
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// =============================================================================
// PUBLISH AGENT MODAL
// =============================================================================

interface SaveAgentModalProps {
  open: boolean;
  onClose: () => void;
  config: AgentConfig;
  onSave: (params: {
    projectId: string;
    name: string;
    description: string;
    tags?: string[];
    capabilities?: string[];
  }) => Promise<void>;
  isSaving: boolean;
  error: string | null;
  userEmail: string;
  defaultProjectId?: string;
}

function SaveAgentModal({
  open,
  onClose,
  config,
  onSave,
  isSaving,
  error,
  userEmail,
  defaultProjectId,
}: SaveAgentModalProps) {
  const [projects, setProjects] = useState<Array<{ project_id: string; name: string }>>([]);
  const [isLoadingProjects, setIsLoadingProjects] = useState(false);
  const [projectsError, setProjectsError] = useState<string | null>(null);
  const [selectedProjectId, setSelectedProjectId] = useState(defaultProjectId || '');
  const [name, setName] = useState(config.name);
  const [description, setDescription] = useState(config.description || '');
  const [tagsInput, setTagsInput] = useState('');
  const [capabilitiesInput, setCapabilitiesInput] = useState((config.capabilities || []).join(', '));

  // Load projects when modal opens
  useEffect(() => {
    if (open && userEmail) {
      setIsLoadingProjects(true);
      setProjectsError(null);
      listProjects(userEmail)
        .then((data) => {
          setProjects(data);
          // Auto-select first project if available and no default
          if (data.length > 0 && !selectedProjectId) {
            setSelectedProjectId(data[0].project_id);
          }
        })
        .catch((err) => {
          console.error('Failed to load projects:', err);
          setProjectsError('Failed to load projects');
          // Use default project if available
          if (defaultProjectId && !selectedProjectId) {
            setSelectedProjectId(defaultProjectId);
            setProjects([{ project_id: defaultProjectId, name: defaultProjectId }]);
          }
        })
        .finally(() => {
          setIsLoadingProjects(false);
        });
    }
  }, [open, userEmail, defaultProjectId]);

  // Sync with config when modal opens
  useEffect(() => {
    if (open) {
      setName(config.name);
      setDescription(config.description || '');
      setCapabilitiesInput((config.capabilities || []).join(', '));
    }
  }, [open, config.name, config.description, config.capabilities]);

  const handleSave = async () => {
    const tags = tagsInput
      .split(',')
      .map((t) => t.trim())
      .filter((t) => t.length > 0);
    const capabilities = capabilitiesInput
      .split(',')
      .map((c) => c.trim())
      .filter((c) => c.length > 0);

    await onSave({
      projectId: selectedProjectId,
      name,
      description,
      tags: tags.length > 0 ? tags : undefined,
      capabilities: capabilities.length > 0 ? capabilities : undefined,
    });
  };

  const isValid = selectedProjectId && name.trim().length > 0 && description.trim().length > 0;

  // Get total tool count
  const selectedToolCount = (config.mcp_servers || []).reduce((count, server) => {
    if (server.selected_tools) {
      return count + server.selected_tools.length;
    }
    return count;
  }, 0);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] flex flex-col">
        <DialogHeader className="shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <SaveIcon className="h-5 w-5 text-primary" />
            Save Agent
          </DialogTitle>
          <DialogDescription>
            Save your agent configuration to a project.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto py-4 space-y-4">
          {/* Row 1: Project (left) | Tags (right) */}
          <div className="grid grid-cols-2 gap-6">
            <div>
              <Label htmlFor="save-project">Project *</Label>
              {projects.length > 0 ? (
                <Select value={selectedProjectId} onValueChange={setSelectedProjectId}>
                  <SelectTrigger className="mt-1" disabled={isLoadingProjects}>
                    <SelectValue placeholder={isLoadingProjects ? 'Loading projects...' : 'Select a project'} />
                  </SelectTrigger>
                  <SelectContent>
                    {projects.map((project) => (
                      <SelectItem key={project.project_id} value={project.project_id}>
                        {project.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  id="save-project"
                  value={selectedProjectId}
                  onChange={(e) => setSelectedProjectId(e.target.value)}
                  placeholder={isLoadingProjects ? 'Loading...' : 'Enter project ID'}
                  className="mt-1"
                  disabled={isLoadingProjects}
                />
              )}
              {projectsError && (
                <p className="text-xs text-amber-600 mt-1">Using default project</p>
              )}
            </div>
            <div>
              <Label htmlFor="save-tags">Tags</Label>
              <Input
                id="save-tags"
                value={tagsInput}
                onChange={(e) => setTagsInput(e.target.value)}
                placeholder="tag1, tag2, tag3 (comma separated)"
                className="mt-1"
              />
            </div>
          </div>

          {/* Row 2: Agent Name (left) | Capabilities (right) */}
          <div className="grid grid-cols-2 gap-6">
            <div>
              <Label htmlFor="save-name">Agent Name *</Label>
              <Input
                id="save-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="My Agent"
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="save-capabilities">Capabilities</Label>
              <Input
                id="save-capabilities"
                value={capabilitiesInput}
                onChange={(e) => setCapabilitiesInput(e.target.value)}
                placeholder="Code generation, Data analysis (comma separated)"
                className="mt-1"
              />
            </div>
          </div>

          {/* Row 3: Description - Full width */}
          <div>
            <Label htmlFor="save-description">Description *</Label>
            <Textarea
              id="save-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe what this agent does, its purpose, and how it should be used..."
              className="mt-1 resize-none w-full"
              rows={3}
            />
          </div>

          {/* Config Summary - Single line footnote */}
          <div className="flex items-center gap-4 text-xs text-muted-foreground pt-2 border-t">
            <span className="font-medium">Config:</span>
            <span className="flex items-center gap-1">
              <SparklesIcon className="h-3 w-3" />
              {config.llm.model}
            </span>
            <span className="flex items-center gap-1">
              <ServerIcon className="h-3 w-3" />
              {config.mcp_servers?.length || 0} MCP
            </span>
            <span className="flex items-center gap-1">
              <WrenchIcon className="h-3 w-3" />
              {selectedToolCount || 'All'} Tools
            </span>
            <span className="flex items-center gap-1">
              <BotIcon className="h-3 w-3" />
              {config.sub_agents?.length || 0} Agents
            </span>
          </div>

          {/* Error Message */}
          {error && (
            <div className="flex items-center gap-2 text-destructive text-sm bg-destructive/10 p-3 rounded-lg">
              <AlertCircleIcon className="h-4 w-4" />
              {error}
            </div>
          )}
        </div>

        <DialogFooter className="shrink-0">
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!isValid || isSaving}>
            {isSaving ? (
              <>
                <Loader2Icon className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <SaveIcon className="h-4 w-4 mr-2" />
                Save
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// =============================================================================
// MAIN COMPONENT - CONTENT
// =============================================================================

function AgentBuilderContent({
  initialConfig,
  initialAgentId,
  initialWorkflowId,
  projectId,
  userEmail,
  tenantId: propTenantId,
  userId: propUserId,
  apiBaseUrl,
  mcpBaseUrl,
  getToken,
  onPublish,
  onSave,
  onCancel,
  onExit,
  skipHome = false,
  className,
}: AgentBuilderPageProps) {
  // Initialize library config
  useEffect(() => {
    setApiBaseUrl(apiBaseUrl);
  }, [apiBaseUrl]);

  useEffect(() => {
    setMcpBaseUrl(mcpBaseUrl);
  }, [mcpBaseUrl]);

  useEffect(() => {
    if (getToken && !isAuthInitialized()) {
      initAuth({ mode: 'external', tokenProvider: getToken });
    }
  }, [getToken]);

  // Set identity if provided
  useEffect(() => {
    if (propTenantId && propUserId && projectId && !isIdentitySet()) {
      setIdentity({
        tenantId: propTenantId,
        projectId: projectId,
        userId: propUserId,
      });
    }
  }, [propTenantId, propUserId, projectId]);

  // Use prop values or defaults
  const tenantId = propTenantId || 'default';
  const userId = propUserId || 'default';
  const effectiveProjectId = projectId || 'default';
  const effectiveEmail = userEmail || `${userId}@example.com`;

  // ==========================================================================
  // STATE
  // ==========================================================================

  // Builder mode state machine
  const [view, setView] = useState<BuilderView>(() => {
    // Skip home if we have initial data or skipHome prop
    if (skipHome || initialAgentId || initialWorkflowId || initialConfig) {
      return 'editor';
    }
    return 'home';
  });
  const [mode, setMode] = useState<BuilderMode>(() => {
    if (initialAgentId) return 'published';
    if (initialWorkflowId) return 'draft';
    return 'new';
  });
  const [workflowId, setWorkflowId] = useState<string | undefined>(initialWorkflowId);
  const [agentId, setAgentId] = useState<string | undefined>(initialAgentId);
  const [deploymentId, setDeploymentId] = useState<string | undefined>();
  const [version, setVersion] = useState<string | undefined>();
  const [baseVersion, setBaseVersion] = useState<string | undefined>(); // When editing published agent

  // Config state
  const [config, setConfig] = useState<AgentConfig>(() => ({
    ...DEFAULT_AGENT_CONFIG,
    ...initialConfig,
  }));
  const [isDirty, setIsDirty] = useState(false);

  // UI state
  const [activeTab, setActiveTab] = useState<BuilderTab>('config');
  const [isInstructionsExpanded, setIsInstructionsExpanded] = useState(false);
  const [chatEnabled, setChatEnabled] = useState(false);
  // Full screen mode: start in full screen if no saved agent
  const [isFullScreen, setIsFullScreen] = useState(false);

  // MCP state
  const [mcpTypes, setMcpTypes] = useState<string[]>([]);
  const [selectedMCPType, setSelectedMCPType] = useState<string | null>(null);
  const [availableMCPServers, setAvailableMCPServers] = useState<MCPServerListItem[]>([]);
  const [mcpToolsCache, setMcpToolsCache] = useState<Record<string, MCPTool[]>>({});
  const [isMCPLoading, setIsMCPLoading] = useState(false);

  // WFM state
  const [availableAgents, setAvailableAgents] = useState<WFMAgent[]>([]);
  const [availableWorkflows, setAvailableWorkflows] = useState<WFMWorkflow[]>([]);
  const [isAgentsLoading, setIsAgentsLoading] = useState(false);
  const [isWorkflowsLoading, setIsWorkflowsLoading] = useState(false);

  // LLM Resources state
  const [availableLLMs, setAvailableLLMs] = useState<LLMResource[]>([]);
  const [isLLMsLoading, setIsLLMsLoading] = useState(false);

  // Instructions modal state
  const [instructionsModalOpen, setInstructionsModalOpen] = useState(false);

  // Save state
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);

  // YAML preview state
  const [yamlPreviewOpen, setYamlPreviewOpen] = useState(false);

  // For the chat runtime: use deploymentId when available (executor fetches from deployment endpoint).
  // Falls back to agentId (published agent), then workflowId (draft — executor resolves via
  // /workflow/get endpoint which loads config directly from the workflow's meta_data).
  const savedAgentId = deploymentId || agentId || workflowId;
  const lastSaved = lastSavedAt;

  // ==========================================================================
  // EFFECTS
  // ==========================================================================

  // Load initial draft if workflow ID provided
  useEffect(() => {
    const loadInitialDraft = async () => {
      if (initialWorkflowId) {
        try {
          const workflow = await loadDraft(effectiveProjectId, initialWorkflowId, effectiveEmail);
          const draftConfig = workflowToConfig(workflow);
          setConfig(draftConfig);
          setWorkflowId(workflow.workflow_id);
          setMode(workflow.is_draft ? 'draft' : 'published');
          setIsDirty(false);
        } catch (error) {
          console.error('Failed to load draft:', error);
        }
      }
    };
    loadInitialDraft();
  }, [initialWorkflowId, effectiveProjectId, effectiveEmail]);

  // Load initial agent if ID provided
  useEffect(() => {
    if (initialAgentId && availableAgents.length > 0) {
      const agent = availableAgents.find(a => a.agent_id === initialAgentId);
      if (agent) {
        const agentConfig = wfmAgentToConfig(agent);
        setConfig(agentConfig);
        setAgentId(agent.agent_id);
        setVersion(agent.version);
        setMode('published');
        setIsDirty(false);

        // Fetch existing deployment for this agent
        getDeployments(agent.agent_id)
          .then((deps) => {
            if (deps.length > 0) {
              setDeploymentId(deps[0].deployment_id);
              console.log('[AgentBuilderPage] Found existing deployment:', deps[0].deployment_id);
            }
          })
          .catch((err) => console.warn('[AgentBuilderPage] Failed to fetch deployments:', err));
      }
    }
  }, [initialAgentId, availableAgents]);

  // Load MCP types on mount (replaces loading all servers flat)
  useEffect(() => {
    loadMCPTypes();
  }, []);

  // Load available agents on mount
  useEffect(() => {
    loadAvailableAgents();
  }, []);

  // Load LLM resources on mount
  useEffect(() => {
    loadLLMResources();
  }, []);

  // Load workflows (drafts) on mount for home view
  useEffect(() => {
    if (view === 'home') {
      loadWorkflows();
    }
  }, [view]);

  // Track dirty state
  useEffect(() => {
    setIsDirty(true);
  }, [config]);

  // Auto-save when in draft mode (debounced)
  useEffect(() => {
    if (mode === 'draft' && workflowId && isDirty) {
      const timer = setTimeout(async () => {
        setIsSaving(true);
        try {
          await updateDraft(effectiveProjectId, workflowId, effectiveEmail, config);
          setLastSavedAt(new Date());
          setIsDirty(false);
        } catch (error) {
          console.error('Auto-save failed:', error);
          setSaveError(error instanceof Error ? error.message : 'Auto-save failed');
        } finally {
          setIsSaving(false);
        }
      }, 2000); // 2 second debounce

      return () => clearTimeout(timer);
    }
  }, [config, mode, workflowId, isDirty, effectiveProjectId, effectiveEmail]);

  // ==========================================================================
  // HANDLERS
  // ==========================================================================

  // Load MCP types (step 1 of the types→instances→tools flow)
  const loadMCPTypes = async () => {
    setIsMCPLoading(true);
    try {
      const types = await listMCPTypes();
      setMcpTypes(types);
    } catch (error) {
      console.error('Failed to load MCP types:', error);
    } finally {
      setIsMCPLoading(false);
    }
  };

  // Load MCP servers filtered by type (step 2)
  const loadMCPServersByType = async (type: string) => {
    setIsMCPLoading(true);
    try {
      const servers = await listMCPServers(type);
      setAvailableMCPServers(servers);
    } catch (error) {
      console.error('Failed to load MCP servers for type:', type, error);
    } finally {
      setIsMCPLoading(false);
    }
  };

  // Handle MCP type selection
  const handleSelectMCPType = (type: string) => {
    if (!type) {
      // Going back to types view
      setSelectedMCPType(null);
      setAvailableMCPServers([]);
      return;
    }
    setSelectedMCPType(type);
    loadMCPServersByType(type);
  };

  // Refresh: reload types and clear selection
  const handleRefreshMCP = () => {
    setSelectedMCPType(null);
    setAvailableMCPServers([]);
    loadMCPTypes();
  };

  // Load MCP tools for a server by resource ID
  // Uses getMCPServerDetails which handles session initialization first
  const loadMCPToolsForServer = async (resourceId: string) => {
    try {
      const { tools } = await getMCPServerDetails(resourceId);
      setMcpToolsCache((prev) => ({ ...prev, [resourceId]: tools }));
    } catch (error) {
      console.error('Failed to load MCP tools:', error);
      // Set empty array so UI stops showing "Loading tools..." spinner
      setMcpToolsCache((prev) => ({ ...prev, [resourceId]: [] }));
    }
  };

  // Load available agents
  const loadAvailableAgents = async () => {
    setIsAgentsLoading(true);
    try {
      const agents = await listAgents({ project_id: effectiveProjectId });
      setAvailableAgents(agents);
    } catch (error) {
      console.error('Failed to load agents:', error);
    } finally {
      setIsAgentsLoading(false);
    }
  };

  // Load LLM resources from WFM
  const loadLLMResources = async () => {
    setIsLLMsLoading(true);
    try {
      const llms = await listLLMResources();
      setAvailableLLMs(llms);
      console.log('[AgentBuilder] Loaded LLM resources:', llms.length, llms);
    } catch (error) {
      console.error('Failed to load LLM resources:', error);
    } finally {
      setIsLLMsLoading(false);
    }
  };

  // Load workflows (drafts and published)
  const loadWorkflows = async () => {
    setIsWorkflowsLoading(true);
    try {
      const workflows = await listWorkflows(effectiveProjectId, effectiveEmail);
      // Filter to only flows_alpha_agent type
      const agentWorkflows = workflows.filter(w => w.workflow_type === 'flows_alpha_agent');
      setAvailableWorkflows(agentWorkflows);
    } catch (error) {
      console.error('Failed to load workflows:', error);
    } finally {
      setIsWorkflowsLoading(false);
    }
  };

  // Save as draft
  const handleSaveDraft = async () => {
    if (mode === 'new') {
      setIsSaving(true);
      setSaveError(null);
      try {
        const result = await createDraft(effectiveProjectId, effectiveEmail, config);
        setWorkflowId(result.workflow_id);
        setMode('draft');
        setLastSavedAt(new Date());
        setIsDirty(false);
      } catch (error) {
        console.error('Failed to save draft:', error);
        setSaveError(error instanceof Error ? error.message : 'Failed to save draft');
      } finally {
        setIsSaving(false);
      }
    }
  };

  // Save agent
  const handleSaveAgent = async (params: {
    projectId: string;
    name: string;
    description: string;
    tags?: string[];
    capabilities?: string[];
  }) => {
    setIsSaving(true);
    setSaveError(null);

    try {
      const saveConfig = {
        ...config,
        name: params.name,
        description: params.description,
        capabilities: params.capabilities || config.capabilities,
        version: '1.0.0', // Default version for new agents
      };

      const result = await createFlowsAlphaAgent(params.projectId, effectiveEmail, saveConfig);

      // Create deployment for the published agent.
      // The deployment_id is used in POST /start (wfm_loader fetches from deployment endpoint).
      // This also auto-creates the run_agent MCP resource enabling sub-agent consumption.
      let depId: string | undefined;
      try {
        const deployment = await createDeployment(
          result.agent_id,
          `${params.name}-default`,
          effectiveEmail,
          {},  // No deployment_fields for skill agents
        );
        depId = deployment.deployment_id;
        console.log('[AgentBuilderPage] Created deployment:', depId);
      } catch (depError) {
        console.error('[AgentBuilderPage] Failed to create deployment (agent still published):', depError);
      }

      setAgentId(result.agent_id);
      setDeploymentId(depId);
      setWorkflowId(result.workflow_id);
      setVersion(result.version);
      setConfig({ ...config, name: params.name, description: params.description });
      setMode('published');
      setLastSavedAt(new Date());
      setIsDirty(false);
      setSaveModalOpen(false);

      if (onPublish) {
        onPublish({
          workflow_id: result.workflow_id,
          agent_id: result.agent_id,
          version: result.version,
          name: result.name,
        });
      }
      // Backwards compatibility
      if (onSave) {
        onSave({
          workflow_id: result.workflow_id,
          agent_id: result.agent_id,
          name: result.name,
        });
      }
    } catch (error) {
      console.error('Failed to save agent:', error);
      setSaveError(error instanceof Error ? error.message : 'Failed to save agent');
    } finally {
      setIsSaving(false);
    }
  };

  // Delete a draft
  const handleDeleteDraft = async (draftWorkflowId: string) => {
    try {
      await deleteDraft(effectiveProjectId, draftWorkflowId, effectiveEmail);
      // Reload workflows
      loadWorkflows();
    } catch (error) {
      console.error('Failed to delete draft:', error);
    }
  };

  // Open a draft for editing
  const handleOpenDraft = async (workflow: WFMWorkflow) => {
    try {
      const draftConfig = workflowToConfig(workflow);
      setConfig(draftConfig);
      setWorkflowId(workflow.workflow_id);
      setMode('draft');
      setIsDirty(false);
      setView('editor');
      setIsFullScreen(true);
    } catch (error) {
      console.error('Failed to open draft:', error);
    }
  };

  // Open a saved agent for editing
  const handleOpenAgent = async (agent: WFMAgent) => {
    const agentConfig = wfmAgentToConfig(agent);
    setConfig(agentConfig);
    setAgentId(agent.agent_id);
    setVersion(agent.version);
    setMode('published');
    setView('editor');
    setIsFullScreen(true);
  };

  // Start new agent
  const handleNewAgent = () => {
    setConfig({ ...DEFAULT_AGENT_CONFIG });
    setWorkflowId(undefined);
    setAgentId(undefined);
    setVersion(undefined);
    setBaseVersion(undefined);
    setMode('new');
    setIsDirty(false);
    setView('editor');
    setIsFullScreen(true);
  };

  // Go back to home
  const handleBackToHome = () => {
    setView('home');
    loadWorkflows();
    loadAvailableAgents();
  };

  // Format relative time
  const formatRelativeTime = (date: Date | string) => {
    const d = typeof date === 'string' ? new Date(date) : date;
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays === 1) return 'Yesterday';
    return `${diffDays} days ago`;
  };

  // Add MCP server with custom handle
  // User provides a unique handle - same server can be added multiple times with different handles
  const handleAddMCPServer = (server: MCPServerListItem, customHandle?: string) => {
    // Generate default handle from server name (sanitized)
    const baseHandle = customHandle || server.name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');

    // Ensure unique handle by appending number if needed
    const existingHandles = (config.mcp_servers || []).map(s => s.handle);
    let handle = baseHandle;
    let counter = 1;
    while (existingHandles.includes(handle)) {
      handle = `${baseHandle}_${counter}`;
      counter++;
    }

    const binding: MCPServerBinding = {
      handle,
      serverName: server.name,
      type: server.type,
      type_template: server.type_template,
      res_id: server.id,
    };
    setConfig({
      ...config,
      mcp_servers: [...(config.mcp_servers || []), binding],
    });
    // Load tools for the added server
    loadMCPToolsForServer(server.id);
  };

  // Remove MCP server by handle
  const handleRemoveMCPServer = (handle: string) => {
    setConfig({
      ...config,
      mcp_servers: (config.mcp_servers || []).filter((s) => s.handle !== handle),
    });
  };

  // Update MCP server (for tool selection changes, handle rename, etc.)
  const handleUpdateMCPServer = (handle: string, updates: Partial<MCPServerBinding>) => {
    // If updating handle, check for uniqueness
    if (updates.handle) {
      const existingHandles = (config.mcp_servers || [])
        .filter(s => s.handle !== handle)
        .map(s => s.handle);
      if (existingHandles.includes(updates.handle)) {
        console.warn('Handle already exists, not updating');
        return;
      }
    }
    setConfig({
      ...config,
      mcp_servers: (config.mcp_servers || []).map((s) =>
        s.handle === handle ? { ...s, ...updates } : s
      ),
    });
  };

  // Add sub agent (with optional deployment from the picker)
  const handleAddSubAgent = (agent: WFMAgent, deploymentId?: string) => {
    const binding: SubAgentBinding = {
      agent_id: agent.agent_id,
      name: agent.name,
      description: agent.description,
      deployment_id: deploymentId,
    };

    setConfig({
      ...config,
      sub_agents: [...(config.sub_agents || []), binding],
    });
  };

  // Update sub agent (e.g., to change deployment)
  const handleUpdateSubAgent = (agentId: string, updates: Partial<SubAgentBinding>) => {
    setConfig({
      ...config,
      sub_agents: (config.sub_agents || []).map((a) =>
        a.agent_id === agentId ? { ...a, ...updates } : a
      ),
    });
  };

  // Remove sub agent
  const handleRemoveSubAgent = (agentId: string) => {
    setConfig({
      ...config,
      sub_agents: (config.sub_agents || []).filter((a) => a.agent_id !== agentId),
    });
  };

  // ==========================================================================
  // RENDER: HOME VIEW
  // ==========================================================================

  // Get drafts and published agents from workflows
  const drafts = availableWorkflows.filter(w => w.is_draft);
  const publishedWorkflows = availableWorkflows.filter(w => !w.is_draft && w.published);

  if (view === 'home') {
    return (
      <div className={cn("flex flex-col h-full w-full bg-background text-foreground", className)}>
        {/* Header */}
        <header className="flex h-14 shrink-0 items-center justify-between border-b border-border bg-card px-6">
          <div className="flex items-center gap-3">
            <SettingsIcon className="h-5 w-5 text-primary" />
            <span className="text-lg font-semibold">Agent Builder</span>
          </div>
          <div className="flex items-center gap-2">
            <ThemeToggleSimple />
            <Button onClick={handleNewAgent}>
              <PlusIcon className="h-4 w-4 mr-2" />
              New Agent
            </Button>
          </div>
        </header>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          <div className="max-w-4xl mx-auto space-y-8">
            {/* My Drafts */}
            <section>
              <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4">
                My Drafts
              </h2>
              {isWorkflowsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2Icon className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : drafts.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground border border-dashed rounded-lg">
                  <FileEditIcon className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>No drafts yet</p>
                  <p className="text-xs mt-1">Create a new agent to get started</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {drafts.map((workflow) => (
                    <div
                      key={workflow.workflow_id}
                      className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
                      onClick={() => handleOpenDraft(workflow)}
                    >
                      <div className="flex items-center gap-3">
                        <FileEditIcon className="h-5 w-5 text-muted-foreground" />
                        <div>
                          <div className="font-medium">{workflow.name || 'Untitled Agent'}</div>
                          <div className="text-xs text-muted-foreground flex items-center gap-2">
                            <Clock3Icon className="h-3 w-3" />
                            Modified {formatRelativeTime(workflow.modified_at)}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteDraft(workflow.workflow_id);
                          }}
                        >
                          <Trash2Icon className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* My Published Agents */}
            <section>
              <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4">
                My Published Agents
              </h2>
              {isAgentsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2Icon className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : availableAgents.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground border border-dashed rounded-lg">
                  <CheckCircle2Icon className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>No published agents yet</p>
                  <p className="text-xs mt-1">Configure and publish your first agent</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {availableAgents.map((agent) => (
                    <div
                      key={agent.agent_id}
                      className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
                      onClick={() => handleOpenAgent(agent)}
                    >
                      <div className="flex items-center gap-3">
                        <CheckCircle2Icon className="h-5 w-5 text-green-500" />
                        <div>
                          <div className="font-medium flex items-center gap-2">
                            {agent.name}
                            {agent.version && (
                              <Badge variant="secondary" className="text-[10px]">
                                v{agent.version}
                              </Badge>
                            )}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {agent.description || 'No description'}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenAgent(agent);
                          }}
                        >
                          Edit
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>
          </div>
        </div>
      </div>
    );
  }

  // ==========================================================================
  // RENDER: EDITOR VIEW
  // ==========================================================================

  // Builder Panel Component (reused in both modes)
  const builderPanel = (
    <Tabs
      value={activeTab}
      onValueChange={(v) => setActiveTab(v as BuilderTab)}
      className="flex-1 flex flex-col overflow-hidden"
    >
      <TabsList className="w-full justify-start px-2 pt-3 pb-0 bg-muted/30 rounded-none h-auto gap-0.5">
        <TabsTrigger value="config" className="text-xs px-2.5 py-1.5 rounded-t-md rounded-b-none data-[state=active]:bg-background">
          Config
        </TabsTrigger>
        <TabsTrigger value="mcp" className="text-xs px-2.5 py-1.5 rounded-t-md rounded-b-none data-[state=active]:bg-background">
          MCP Servers
          {(config.mcp_servers?.length || 0) > 0 && (
            <Badge variant="secondary" className="ml-1 text-[10px] h-4 px-1">
              {config.mcp_servers?.length}
            </Badge>
          )}
        </TabsTrigger>
        <TabsTrigger value="subagents" className="text-xs px-2.5 py-1.5 rounded-t-md rounded-b-none data-[state=active]:bg-background">
          Sub Agents
          {(config.sub_agents?.length || 0) > 0 && (
            <Badge variant="secondary" className="ml-1 text-[10px] h-4 px-1">
              {config.sub_agents?.length}
            </Badge>
          )}
        </TabsTrigger>
      </TabsList>

      <TabsContent value="config" className="flex-1 overflow-auto m-0">
        <AgentConfigForm
          config={config}
          onChange={setConfig}
          onExpandInstructions={() => setInstructionsModalOpen(true)}
          availableLLMs={availableLLMs}
          isLLMsLoading={isLLMsLoading}
        />
      </TabsContent>

      <TabsContent value="mcp" className="flex-1 overflow-auto m-0">
        <MCPServersPanel
          selectedServers={config.mcp_servers || []}
          onAddServer={handleAddMCPServer}
          onRemoveServer={handleRemoveMCPServer}
          onUpdateServer={handleUpdateMCPServer}
          availableServers={availableMCPServers}
          mcpTypes={mcpTypes}
          toolsCache={mcpToolsCache}
          isLoading={isMCPLoading}
          onRefresh={handleRefreshMCP}
          onLoadTools={loadMCPToolsForServer}
          onSelectType={handleSelectMCPType}
          selectedMCPType={selectedMCPType}
        />
      </TabsContent>

      <TabsContent value="subagents" className="flex-1 overflow-auto m-0">
        <SubAgentsPanel
          selectedAgents={config.sub_agents || []}
          onAddAgent={handleAddSubAgent}
          onRemoveAgent={handleRemoveSubAgent}
          availableAgents={availableAgents}
          isLoading={isAgentsLoading}
          onRefresh={loadAvailableAgents}
        />
      </TabsContent>
    </Tabs>
  );

  return (
    <div className={cn("flex flex-col h-full w-full bg-background text-foreground", className)}>
      {/* Header */}
      <header className="flex h-12 shrink-0 items-center justify-between border-b border-border bg-card px-4">
        <div className="flex items-center gap-3">
          {/* Back button */}
          {!skipHome && (
            <Button variant="ghost" size="sm" onClick={handleBackToHome} className="mr-1">
              <ArrowLeftIcon className="h-4 w-4" />
            </Button>
          )}
          <SettingsIcon className="h-5 w-5 text-primary" />
          <span className="font-semibold">Agent Builder</span>

          {/* Mode badge */}
          {mode === 'draft' && (
            <Badge variant="secondary" className="text-xs">
              Draft
            </Badge>
          )}
          {mode === 'published' && version && (
            <Badge variant="outline" className="text-xs">
              Published v{version}
            </Badge>
          )}
          {baseVersion && (
            <Badge variant="outline" className="text-xs text-muted-foreground">
              based on v{baseVersion}
            </Badge>
          )}

          {/* Agent name */}
          {config.name && (
            <span className="text-sm text-muted-foreground">{config.name}</span>
          )}

          {/* Save status */}
          {isSaving && (
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              <Loader2Icon className="h-3 w-3 animate-spin" />
              Saving...
            </span>
          )}
          {!isSaving && lastSavedAt && (
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              <CheckCircle2Icon className="h-3 w-3 text-green-500" />
              Saved {formatRelativeTime(lastSavedAt)}
            </span>
          )}
          {!isSaving && isDirty && mode === 'new' && (
            <Badge variant="secondary" className="text-[10px]">
              Unsaved
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          <ThemeToggleSimple />

          {/* Expand/Collapse button */}
          {(mode === 'draft' || mode === 'published') && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsFullScreen(!isFullScreen)}
              title={isFullScreen ? 'Show chat panel' : 'Expand config'}
            >
              {isFullScreen ? (
                <Minimize2Icon className="h-4 w-4" />
              ) : (
                <Maximize2Icon className="h-4 w-4" />
              )}
            </Button>
          )}

          {/* View YAML */}
          <Button variant="outline" size="sm" onClick={() => setYamlPreviewOpen(true)}>
            <CodeIcon className="h-4 w-4 mr-1" />
            YAML
          </Button>

          {/* Save button */}
          <Button size="sm" onClick={() => setSaveModalOpen(true)} disabled={isSaving}>
            <SaveIcon className="h-4 w-4 mr-1" />
            Save
          </Button>
        </div>
      </header>

      {/* Main Content - Full Screen Mode */}
      {isFullScreen ? (
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Full Screen Tabs */}
          <Tabs
            value={activeTab}
            onValueChange={(v) => setActiveTab(v as BuilderTab)}
            className="flex-1 flex flex-col overflow-hidden"
          >
            <div className="shrink-0 px-6 pt-4 bg-background">
              <TabsList className="bg-muted/50 p-1 rounded-lg h-auto gap-1">
                <TabsTrigger
                  value="config"
                  className="text-sm px-5 py-2 rounded-md data-[state=active]:bg-background data-[state=active]:shadow-sm"
                >
                  Agent Config
                </TabsTrigger>
                <TabsTrigger
                  value="mcp"
                  className="text-sm px-5 py-2 rounded-md data-[state=active]:bg-background data-[state=active]:shadow-sm"
                >
                  MCP Servers
                  {(config.mcp_servers?.length || 0) > 0 && (
                    <Badge variant="secondary" className="ml-2 text-[10px] h-4 px-1.5">
                      {config.mcp_servers?.length}
                    </Badge>
                  )}
                </TabsTrigger>
                <TabsTrigger
                  value="subagents"
                  className="text-sm px-5 py-2 rounded-md data-[state=active]:bg-background data-[state=active]:shadow-sm"
                >
                  Sub Agents
                  {(config.sub_agents?.length || 0) > 0 && (
                    <Badge variant="secondary" className="ml-2 text-[10px] h-4 px-1.5">
                      {config.sub_agents?.length}
                    </Badge>
                  )}
                </TabsTrigger>
              </TabsList>
            </div>

            <TabsContent value="config" className="flex-1 overflow-hidden m-0 bg-background">
              <FullScreenConfigForm
                config={config}
                onChange={setConfig}
                availableLLMs={availableLLMs}
                isLLMsLoading={isLLMsLoading}
              />
            </TabsContent>

            <TabsContent value="mcp" className="flex-1 overflow-hidden m-0 bg-background">
              <FullScreenMCPPanel
                selectedServers={config.mcp_servers || []}
                availableServers={availableMCPServers}
                mcpTypes={mcpTypes}
                toolsCache={mcpToolsCache}
                isLoading={isMCPLoading}
                onAddServer={handleAddMCPServer}
                onRemoveServer={handleRemoveMCPServer}
                onUpdateServer={handleUpdateMCPServer}
                onRefresh={handleRefreshMCP}
                onLoadTools={loadMCPToolsForServer}
                onSelectType={handleSelectMCPType}
                selectedMCPType={selectedMCPType}
              />
            </TabsContent>

            <TabsContent value="subagents" className="flex-1 overflow-hidden m-0 bg-background">
              <FullScreenSubAgentsPanel
                selectedAgents={config.sub_agents || []}
                availableAgents={availableAgents}
                isLoading={isAgentsLoading}
                onAddAgent={handleAddSubAgent}
                onRemoveAgent={handleRemoveSubAgent}
                onUpdateAgent={handleUpdateSubAgent}
                onRefresh={loadAvailableAgents}
              />
            </TabsContent>
          </Tabs>

          {/* Bottom Action Bar */}
          <div className="shrink-0 py-3 px-6 bg-muted/30 flex items-center justify-end gap-3">
            {savedAgentId && (
              <span className="text-sm text-muted-foreground flex items-center gap-2 mr-auto">
                <CheckCircle2Icon className="h-4 w-4 text-green-500" />
                Agent saved
              </span>
            )}
            <Button
              variant="outline"
              onClick={() => setYamlPreviewOpen(true)}
            >
              <CodeIcon className="h-4 w-4 mr-2" />
              Preview YAML
            </Button>
            <Button
              onClick={() => {
                if (savedAgentId) {
                  setIsFullScreen(false);
                  setChatEnabled(true);
                } else {
                  setSaveModalOpen(true);
                }
              }}
            >
              <MessageSquareIcon className="h-4 w-4 mr-2" />
              {savedAgentId ? 'Start Chatting' : 'Save & Test'}
            </Button>
          </div>
        </div>
      ) : (
        /* Condensed Mode - Split View */
        <div className="flex-1 flex overflow-hidden">
          {/* Builder Panel */}
          <div className="w-[390px] shrink-0 border-r border-border flex flex-col overflow-hidden">
            {builderPanel}
          </div>

          {/* Chat Panel */}
          <div className="flex-1 flex flex-col overflow-hidden">
            <ChatTestPanel
              config={config}
              enabled={chatEnabled}
              onEnable={() => setChatEnabled(true)}
              tenantId={tenantId}
              projectId={effectiveProjectId}
              userId={userId}
              apiBaseUrl={apiBaseUrl}
              savedAgentId={savedAgentId}
              availableLLMs={availableLLMs}
              getToken={getToken}
            />
          </div>
        </div>
      )}

      <YamlPreviewModal
        open={yamlPreviewOpen}
        onClose={() => setYamlPreviewOpen(false)}
        config={config}
        toolsCache={mcpToolsCache}
      />

      {/* Save Modal */}
      <SaveAgentModal
        open={saveModalOpen}
        onClose={() => setSaveModalOpen(false)}
        config={config}
        onSave={handleSaveAgent}
        isSaving={isSaving}
        error={saveError}
        userEmail={effectiveEmail}
        defaultProjectId={effectiveProjectId}
      />

      {/* Instructions Modal */}
      <InstructionsModal
        open={instructionsModalOpen}
        onClose={() => setInstructionsModalOpen(false)}
        instructions={config.instructions}
        onChange={(instructions) => setConfig({ ...config, instructions })}
      />
    </div>
  );
}

// =============================================================================
// MAIN EXPORT
// =============================================================================

/**
 * AgentBuilderPage - Visual agent configuration interface
 * Wraps content with ThemeProvider
 */
export function AgentBuilderPage(props: AgentBuilderPageProps) {
  return (
    <ThemeProvider>
      <AgentBuilderContent {...props} />
    </ThemeProvider>
  );
}
